import './initZbarWasm.js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './i18n.js';
import { App } from './App.js';
import { applyInitialCustomerTheme, ThemeProvider } from './app/ThemeProvider.js';
import { ErrorBoundary } from './shared/components/ErrorBoundary/ErrorBoundary.js';
import { CustomerUnknownErrorFallback } from './shared/components/ErrorBoundary/CustomerUnknownErrorFallback.js';
import { captureException, initSentry } from 'pi-kiosk-shared/sentry';
import { loadRuntimeConfig } from './config/runtimeConfig.js';
import { customerLogger } from './shared/logging/customerLogger.js';
import { installCustomerHttpStormFetchGuard } from './shared/http/stormAwareFetch.js';
import {
  noteRenderStormSignature,
} from './shared/render/renderStormLatch.js';
import {
  reportDedupedUnhandledRejection,
  reportDedupedWindowError,
} from './shared/render/windowErrorDedup.js';
import { VclProvider } from './vcl/index.js';
import './styles/globals.css';


// Wave D / G15 — residual raw fetch islands honor HTTP_STORM_DETECTED (Nominatim/blob bypass).
installCustomerHttpStormFetchGuard();

const config = loadRuntimeConfig();

applyInitialCustomerTheme(config);

try {
  initSentry({
    dsn: config.sentryDsn ?? import.meta.env.VITE_SENTRY_DSN,
    environment: config.sentryEnvironment ?? import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: config.buildVersion,
    app: 'customer',
    isProd: import.meta.env.PROD,
  });
} catch (err) {
  customerLogger.warn('Sentry init failed', { error: err, module: 'main' });
}

/** Global async/window bridge — deduped log + Sentry; arms render-storm latch on #185. */
window.addEventListener('error', (event: ErrorEvent): void => {
  const error =
    event.error instanceof Error ? event.error : new Error(event.message || 'Window error');
  noteRenderStormSignature(error, 'window.error');
  const meta = {
    module: 'main',
    source: 'window.error',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  };
  reportDedupedWindowError({
    error,
    meta,
    log: (message, err, logMeta) => {
      customerLogger.error(message, err, logMeta);
    },
    capture: (err, captureMeta) => {
      captureException(err, captureMeta);
    },
  });
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
  const reason = event.reason;
  const error = reason instanceof Error ? reason : new Error(String(reason));
  noteRenderStormSignature(error, 'window.unhandledrejection');
  const meta = {
    module: 'main',
    source: 'window.unhandledrejection',
  };
  reportDedupedUnhandledRejection({
    error,
    meta,
    log: (message, err, logMeta) => {
      customerLogger.error(message, err, logMeta);
    },
    capture: (err, captureMeta) => {
      captureException(err, captureMeta);
    },
  });
});

if (import.meta.env.DEV && import.meta.env.VITE_A11Y_AUDIT === '1') {
  void import('./dev/axe.js').then((mod) => mod.installAxeAudit());
}

const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root element missing in index.html');
}

ReactDOM.createRoot(container).render(
  <React.StrictMode>
    <ThemeProvider lightOverrideEnabled>
      {/*
        VclProvider gates via shouldMountVcl() — always wrap is OK (no-op outside
        DEV + ?vcl=1 / localStorage opt-in). Inside ThemeProvider so VCL CSS vars
        sit under the live customer theme aliases (no shared theme write-through).
      */}
      <VclProvider>
        {/*
          useTransitions={false}: RR7 defaults to startTransition for history updates.
          Leaflet map work can starve that deferred commit — address bar drops view=map
          while React LocationContext stays on map, so Seznam/list toggles appear broken.
          See https://reactrouter.com/explanation/react-transitions
        */}
        <BrowserRouter useTransitions={false}>
          <ErrorBoundary
            fallback={<CustomerUnknownErrorFallback />}
            observability={{
              boundary_layer: 'L1',
              msId: 'MS-REC-076',
              feature: 'shell',
            }}
          >
            <App config={config} />
          </ErrorBoundary>
        </BrowserRouter>
      </VclProvider>
    </ThemeProvider>
  </React.StrictMode>
);
